import React, { useEffect } from 'react';

import { useIsMobile } from '@uplift/hooks';
import {
  FadeAndSlide, FadeAndSlideTransitionDirection, SubscribeGoogleCalendarForm, useAnimatedState
} from '@uplift/ui-v2';

import { FUEAvailabilityForm } from './FUEAvailabilityForm/FUEAvailabilityForm';
import { FUEIdealCaseloadForm } from './FUEIdealCaseloadForm';
import { useQueryClient } from '@tanstack/react-query';
import { useCurrentuser } from '@uplift/contexts';
import { useOnboardingEventActions } from '../../onboarding-dashboard/useOnboardingEventActions.hook';

export type ScheduleConfigurationContentProps = {
  idealCaseload?: number;
  onStateUpdate: (newState: string) => void;
};


export const ScheduleConfigurationContent: React.VFC<ScheduleConfigurationContentProps> = ({ idealCaseload, onStateUpdate }) => {
  const isMobile = useIsMobile('sm');
  const queryClient = useQueryClient();
  const { state, animateIn, flow, handleExited, goTo } = useAnimatedState<'idealCaseload' | 'availability' | 'calendarIntegration'>('idealCaseload');
  const transitionDirection: FadeAndSlideTransitionDirection = {
    next: 'left-left' as const,
    back: 'right-right' as const,
  }[flow] ?? 'no-no' as const;
  const me = useCurrentuser();
  const { matchingAvailabilitySubmitted } = useOnboardingEventActions(me?.id);

  useEffect(() => {
    if (onStateUpdate) {
      onStateUpdate(state);
    }
  }, [ onStateUpdate, state ]);

  if (state === 'idealCaseload') {
    return (
      <FadeAndSlide in={animateIn} direction={transitionDirection} onExited={handleExited}>
        <FUEIdealCaseloadForm
          idealCaseload={idealCaseload}
          disabled={!animateIn}
          onSuccess={() => goTo('next', 'availability')}
        />
      </FadeAndSlide>
    );
  }

  if (state === 'availability') {

    if (!isMobile) {
      return (
        <FadeAndSlide in={animateIn} direction={transitionDirection} onExited={handleExited}>
          <FUEAvailabilityForm
            idealCaseload={idealCaseload}
            animateIn={animateIn}
            onBack={() => goTo('back', 'idealCaseload')}
            onSuccess={() => {
              matchingAvailabilitySubmitted();
              goTo('next', 'calendarIntegration');
            }}
            nextStepActionFooter
          />
        </FadeAndSlide>
      );
    }

    return (
      <FadeAndSlide in={animateIn} direction={transitionDirection} onExited={handleExited}>
        <FUEAvailabilityForm
          idealCaseload={idealCaseload}
          animateIn={animateIn}
          onBack={() => goTo('back', 'idealCaseload')}
          onSuccess={() => goTo('next', () => {
            matchingAvailabilitySubmitted();
            queryClient.invalidateQueries([ 'fueCompleted' ]);
          })}
        />
      </FadeAndSlide>
    );
  }

  if (state === 'calendarIntegration') {
    return (
      <FadeAndSlide in={animateIn} direction={transitionDirection} onExited={handleExited}>
        <SubscribeGoogleCalendarForm
          disabled={!animateIn}
          onSkip={() => goTo('next', () => {
            queryClient.invalidateQueries([ 'fueCompleted' ]);
          })}
          onSuccess={() => goTo('next', () => {
            queryClient.invalidateQueries([ 'fueCompleted' ]);
          })}
          onBack={() => goTo('back', 'availability')}
        />
      </FadeAndSlide>
    );
  }

  return null;
};
